import { DateTime } from "luxon";
import { tpePaymentMethodLabels } from "cadendar-shared";
import type { TpePaymentMethod } from "cadendar-shared";
import { useState } from "react";
import { trpc } from "../../main/components/trpc.ts";
import {
  CompanionError,
  companionHealth,
  companionTerminalStatus,
  getCompanionSettings,
  saveCompanionSettings,
} from "../companionClient.ts";
import {
  formatMoneyCents,
  formatSettledCount,
  isSettledTpePayment,
} from "../display.ts";
import { TpePaymentsTable } from "../components/TpePaymentsTable.tsx";
import type { TpePaymentsTableRow } from "../components/TpePaymentsTable.tsx";

type TpePaymentCategory = "sansContact" | "insertion" | "amex" | "unspecified";

const categoryKeys = [
  "sansContact",
  "insertion",
  "amex",
  "unspecified",
] as const;

const categoryLabels: Record<TpePaymentCategory, string> = {
  sansContact: tpePaymentMethodLabels.sansContact,
  insertion: tpePaymentMethodLabels.insertion,
  amex: tpePaymentMethodLabels.amex,
  unspecified: "Non précisé",
};

const effectiveCategory = (payment: {
  payment_method: TpePaymentMethod | null;
}): TpePaymentCategory => payment.payment_method ?? "unspecified";

export function TpePaymentsDayPage() {
  const initialDate = DateTime.now().setZone("Europe/Paris").toISODate() ?? "";
  const [date, setDate] = useState(initialDate);
  const [port, setPort] = useState(() => String(getCompanionSettings().port));
  const [token, setToken] = useState(() => getCompanionSettings().token);
  const [settingsMessage, setSettingsMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const dayQuery = trpc.tpePayment.getByDay.useQuery({ date });
  const payments = [...(dayQuery.data ?? [])].sort((left, right) => {
    const leftInitiatedAt =
      left.initiated_at instanceof Date
        ? DateTime.fromJSDate(left.initiated_at)
        : DateTime.fromISO(left.initiated_at);
    const rightInitiatedAt =
      right.initiated_at instanceof Date
        ? DateTime.fromJSDate(right.initiated_at)
        : DateTime.fromISO(right.initiated_at);
    return leftInitiatedAt.toMillis() - rightInitiatedAt.toMillis();
  });

  const utils = trpc.useUtils();
  const setPaymentMethodMutation = trpc.tpePayment.setPaymentMethod.useMutation(
    {},
  );
  const unlockForCorrectionMutation =
    trpc.tpePayment.unlockForCorrection.useMutation({});
  const cancelCorrectionMutation = trpc.tpePayment.cancelCorrection.useMutation(
    {},
  );
  const saveCorrectionMutation = trpc.tpePayment.saveCorrection.useMutation({});

  const handleSetPaymentMethod = async (
    paymentId: string,
    paymentMethod: TpePaymentMethod,
  ) => {
    try {
      await setPaymentMethodMutation.mutateAsync({ paymentId, paymentMethod });
      await utils.tpePayment.invalidate();
    } catch (caught) {
      setSettingsMessage({
        type: "error",
        text:
          caught instanceof Error
            ? caught.message
            : "Impossible d'enregistrer le mode de paiement.",
      });
      throw caught;
    }
  };

  const handleUnlockForCorrection = async (paymentId: string) => {
    try {
      await unlockForCorrectionMutation.mutateAsync({ paymentId });
      await utils.tpePayment.invalidate();
    } catch (caught) {
      setSettingsMessage({
        type: "error",
        text:
          caught instanceof Error
            ? caught.message
            : "Impossible de déverrouiller le règlement.",
      });
      throw caught;
    }
  };

  const handleCancelCorrection = async (paymentId: string) => {
    try {
      await cancelCorrectionMutation.mutateAsync({ paymentId });
      await utils.tpePayment.invalidate();
    } catch (caught) {
      setSettingsMessage({
        type: "error",
        text:
          caught instanceof Error
            ? caught.message
            : "Impossible d'annuler la modification.",
      });
      throw caught;
    }
  };

  const handleSaveCorrection = async (options: {
    paymentId: string;
    paymentMethod: TpePaymentMethod;
    accountingAmountCents: number;
  }) => {
    try {
      await saveCorrectionMutation.mutateAsync(options);
      await utils.tpePayment.invalidate();
    } catch (caught) {
      setSettingsMessage({
        type: "error",
        text:
          caught instanceof Error
            ? caught.message
            : "Impossible d'enregistrer la modification.",
      });
      throw caught;
    }
  };

  const settledPayments = payments.filter((payment) =>
    isSettledTpePayment(payment.status),
  );
  const settledTotal = settledPayments.reduce(
    (sum, payment) => sum + payment.accounting_amount_cents,
    0,
  );

  const paymentsByCategory = new Map<TpePaymentCategory, TpePaymentsTableRow[]>(
    categoryKeys.map((key) => [key, [] as TpePaymentsTableRow[]]),
  );
  const settledByCategory = new Map<
    TpePaymentCategory,
    { count: number; amountCents: number }
  >();
  for (const payment of payments) {
    const key = effectiveCategory(payment);
    paymentsByCategory.get(key)?.push(payment);
  }
  for (const payment of settledPayments) {
    const key = effectiveCategory(payment);
    const summary = settledByCategory.get(key) ?? {
      count: 0,
      amountCents: 0,
    };
    summary.count += 1;
    summary.amountCents += payment.accounting_amount_cents;
    settledByCategory.set(key, summary);
  }

  const shiftDay = (delta: number) => {
    const current = DateTime.fromISO(date, { zone: "Europe/Paris" });
    const next = current.plus({ days: delta });
    setDate(next.toISODate() ?? "");
  };

  const handleSaveSettings = () => {
    const portNumber = Number(port);
    if (!Number.isInteger(portNumber) || portNumber < 1 || portNumber > 65535) {
      setSettingsMessage({
        type: "error",
        text: "Le port doit être un entier entre 1 et 65535.",
      });
      return;
    }
    if (!token.trim()) {
      setSettingsMessage({
        type: "error",
        text: "Le token d'appairage ne peut pas être vide.",
      });
      return;
    }
    saveCompanionSettings({ port: portNumber, token: token.trim() });
    setSettingsMessage({
      type: "success",
      text: "Réglages TPE enregistrés.",
    });
  };

  const handleTestCompanion = async () => {
    handleSaveSettings();
    setSettingsMessage(null);
    try {
      const health = await companionHealth();
      const terminal = await companionTerminalStatus();
      if (!terminal.configured) {
        setSettingsMessage({
          type: "error",
          text: "Companion joignable, mais le TPE n'est pas configuré dans le companion.",
        });
        return;
      }
      setSettingsMessage({
        type: "success",
        text: `Companion ${health.version} joignable. TPE ${
          terminal.reachable ? "accessible" : "injoignable"
        } (${terminal.host}:${terminal.port}).`,
      });
    } catch (caught) {
      setSettingsMessage({
        type: "error",
        text:
          caught instanceof CompanionError
            ? caught.message
            : caught instanceof Error
              ? caught.message
              : "Erreur inattendue.",
      });
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-lg font-semibold text-slate-800">
        Règlements CB par jour
      </h1>

      <div className="mt-4 rounded-lg border border-slate-200 p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Réglages companion
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Port de l'API locale et token d'appairage affichés dans l'onglet TPE
          du companion.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Port API locale
            </label>
            <input
              type="number"
              value={port}
              onChange={(e) => setPort(e.target.value)}
              className="w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="min-w-64 flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Token d'appairage
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            />
          </div>
          <button
            type="button"
            onClick={handleSaveSettings}
            className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white"
          >
            Enregistrer
          </button>
          <button
            type="button"
            onClick={handleTestCompanion}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700"
          >
            Tester la connexion
          </button>
        </div>
        {settingsMessage ? (
          <div
            className={`mt-3 rounded-lg p-3 text-sm ${
              settingsMessage.type === "success"
                ? "bg-green-50 text-green-800"
                : "bg-red-50 text-red-800"
            }`}
          >
            {settingsMessage.text}
          </div>
        ) : null}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => shiftDay(-1)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
        >
          Jour précédent
        </button>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => shiftDay(1)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
        >
          Jour suivant
        </button>
        <button
          type="button"
          onClick={() => setDate(initialDate)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
        >
          Aujourd'hui
        </button>
        <span className="ml-auto text-sm font-semibold text-slate-700">
          Total encaissé : {formatMoneyCents(settledTotal)}
        </span>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Récapitulatif par mode de paiement
        </h2>
        {settledPayments.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            Aucun règlement encaissé ce jour.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-3">
            {categoryKeys.map((key) => {
              const summary = settledByCategory.get(key);
              if (!summary) {
                return null;
              }
              return (
                <div
                  key={key}
                  className="rounded-lg bg-slate-50 px-3 py-2 text-sm"
                >
                  <span className="text-slate-500">
                    {categoryLabels[key]} :{" "}
                  </span>
                  <span className="font-semibold text-slate-800">
                    {formatSettledCount(summary.count)},{" "}
                    {formatMoneyCents(summary.amountCents)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-6">
        {dayQuery.isPending ? (
          <p className="text-sm text-slate-500">Chargement...</p>
        ) : payments.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun règlement CB ce jour.</p>
        ) : (
          categoryKeys.map((key) => {
            const categoryPayments = paymentsByCategory.get(key) ?? [];
            if (categoryPayments.length === 0) {
              return null;
            }
            const summary = settledByCategory.get(key);
            return (
              <section key={key}>
                <div className="flex flex-wrap items-baseline gap-2 border-b border-slate-200 pb-1">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    {categoryLabels[key]}
                  </h2>
                  <span className="text-sm text-slate-600">
                    {summary
                      ? `${formatSettledCount(summary.count)} · Total : ${formatMoneyCents(summary.amountCents)}`
                      : "Aucun règlement encaissé"}
                  </span>
                </div>
                <TpePaymentsTable
                  payments={categoryPayments}
                  onSetPaymentMethod={handleSetPaymentMethod}
                  onUnlockForCorrection={handleUnlockForCorrection}
                  onCancelCorrection={handleCancelCorrection}
                  onSaveCorrection={handleSaveCorrection}
                  isPending={
                    setPaymentMethodMutation.isPending ||
                    unlockForCorrectionMutation.isPending ||
                    cancelCorrectionMutation.isPending ||
                    saveCorrectionMutation.isPending
                  }
                />
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
